import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, formatPrice, imgUrl } from "@/lib/api";
import { PageHeader, Panel, DarkBtn, ConfirmDelete, ImageUpload, Field } from "@/components/admin/AdminUI";

const TAGS = ["Ev Yemeği", "Premium", "Usta Tavsiyesi", "7/24", "Acılı", "Vejetaryen"];
const blank = (cat) => ({ category_id: cat || "", name: "", description: "", price: 0, image: "", tags: [], featured: false, available: true, order: 0, allergens: "", calories: "" });

function ItemForm({ initial, categories, onSaved, onClose }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleTag = (t) => set("tags", f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.category_id) return toast.error("Kategori seçin");
    setSaving(true);
    try {
      const body = { ...f, price: Number(f.price), order: Number(f.order) };
      delete body.id;
      delete body.created_at;
      if (f.id) await api.put(`/admin/menu-items/${f.id}`, body);
      else await api.post("/admin/menu-items", body);
      toast.success("Ürün kaydedildi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="menu-item-form">
      <Field label="Görsel"><ImageUpload value={f.image} onChange={(v) => set("image", v)} testid="menu-item-image" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Ürün adı *"><Input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="menu-item-name-input" /></Field>
        <Field label="Kategori *">
          <select required value={f.category_id} onChange={(e) => set("category_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm" data-testid="menu-item-category-select">
            <option value="">Seçin…</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Açıklama"><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="menu-item-description-input" /></Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Fiyat (₺, KDV dahil)"><Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="menu-item-price-input" /></Field>
        <Field label="Sıra"><Input type="number" value={f.order} onChange={(e) => set("order", e.target.value)} data-testid="menu-item-order-input" /></Field>
        <Field label="Kalori"><Input value={f.calories} onChange={(e) => set("calories", e.target.value)} placeholder="örn. 450 kcal" data-testid="menu-item-calories-input" /></Field>
      </div>
      <Field label="Alerjenler"><Input value={f.allergens} onChange={(e) => set("allergens", e.target.value)} placeholder="örn. Gluten, süt ürünü" data-testid="menu-item-allergens-input" /></Field>
      <Field label="Etiketler">
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t) => (
            <button type="button" key={t} onClick={() => toggleTag(t)} className={`rounded-full border px-3 py-1 text-xs ${f.tags.includes(t) ? "bg-[#1A1513] text-[#D4AF37] border-[#1A1513]" : "border-[#E5DCD0]"}`} data-testid={`menu-item-tag-${t}`}>{t}</button>
          ))}
        </div>
      </Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} data-testid="menu-item-featured-switch" /> Ana sayfada öne çıkar</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.available} onCheckedChange={(v) => set("available", v)} data-testid="menu-item-available-switch" /> Satışta (kapalıysa "Tükendi")</label>
      </div>
      <DarkBtn type="submit" disabled={saving} className="w-full" data-testid="menu-item-save-btn">{saving ? "Kaydediliyor…" : "Kaydet"}</DarkBtn>
    </form>
  );
}

export default function MenuItems() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/admin/menu-items"), api.get("/admin/categories")]);
    setItems(a.data);
    setCats(b.data);
  };
  useEffect(() => {
    load();
  }, []);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const shown = useMemo(
    () => items.filter((i) => (!filter || i.category_id === filter) && i.name.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr"))),
    [items, filter, q]
  );

  const quick = async (it, patch) => {
    const body = { ...it, ...patch };
    delete body.id;
    delete body.created_at;
    try {
      await api.put(`/admin/menu-items/${it.id}`, body);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/menu-items/${id}`);
    toast.success("Ürün silindi");
    load();
  };

  return (
    <div data-testid="admin-menu-page">
      <PageHeader title="Menü Ürünleri" desc={`${items.length} ürün · Fiyatlar QR menüye anında yansır.`}>
        <DarkBtn onClick={() => setEditing(blank(filter))} data-testid="admin-menu-add-btn"><Plus size={16} /> Yeni Ürün</DarkBtn>
      </PageHeader>
      <Panel className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B5E56]" />
          <Input className="pl-9" placeholder="Ürün ara…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-menu-search-input" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="admin-menu-category-filter">
          <option value="">Tüm kategoriler</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Panel>
      <Panel className="divide-y divide-[#E5DCD0]">
        {!shown.length && <p className="p-8 text-center text-sm text-[#6B5E56]">Ürün bulunamadı.</p>}
        {shown.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`admin-menu-row-${it.id}`}>
            <div className="h-14 w-14 rounded-md bg-[#F3EDE2] overflow-hidden shrink-0">{it.image && <img src={imgUrl(it.image)} alt="" className="h-full w-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-snug line-clamp-2 flex items-center gap-2">{it.name} {it.featured && <Star size={13} className="text-[#D4AF37]" fill="currentColor" />}</p>
              <p className="text-xs text-[#6B5E56]">{catName(it.category_id)} · {it.tags.join(", ") || "etiket yok"}</p>
            </div>
            <p className="font-display text-sm whitespace-nowrap">{formatPrice(it.price)}</p>
            <label className="flex items-center gap-2 text-xs text-[#6B5E56]"><Switch checked={it.available} onCheckedChange={(v) => quick(it, { available: v })} data-testid={`admin-menu-available-${it.id}`} /> <span className="hidden sm:inline">Satışta</span></label>
            <button onClick={() => setEditing(it)} className="p-2 rounded-md hover:bg-[#F3EDE2]" aria-label="Düzenle" data-testid={`admin-menu-edit-${it.id}`}><Pencil size={15} /></button>
            <ConfirmDelete onConfirm={() => remove(it.id)} testid={`admin-menu-delete-${it.id}`}>
              <button className="p-2 rounded-md hover:bg-red-50 text-[#C84B31]" aria-label="Sil" data-testid={`admin-menu-delete-${it.id}`}><Trash2 size={15} /></button>
            </ConfirmDelete>
          </div>
        ))}
      </Panel>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-ym text-2xl">{editing?.id ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          {editing && <ItemForm initial={editing} categories={cats} onSaved={load} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
